import React, { useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Alert, Chip } from '@mui/material';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TerminalIcon from '@mui/icons-material/Terminal';
import WifiIcon from '@mui/icons-material/Wifi';
import { useLanguage } from '../context/LanguageContext';

const initialLogs = [
    'System initialization successful. Node connection online.',
    'AWS Cloud Storage bucket connected: s3://dinexa-saas-prod-db',
    'Local SQLite Database cached. File size: 4.82 MB',
    'Sync completed: 14 pending sales records sent.',
    'Automated hourly schema integrity check verified (100% match).'
];

export default function CloudSaaSView() {
    const { t, currentLanguage } = useLanguage();
    const [syncing, setSyncing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [logs, setLogs] = useState(initialLogs);

    const handleManualBackup = () => {
        setSyncing(true);
        setProgress(10);
        
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setSyncing(false);
                    const now = new Date().toLocaleTimeString();
                    setLogs(old => [`[${now}] Manual cloud DB backup completed successfully. Archive size: 5.12 MB.`, ...old]);
                    return 100;
                }
                return prev + 30;
            });
        }, 600);
    };

    return (
        <Box sx={{ p: 4, maxWidth: 1200, margin: '0 auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: '#008751' }}>
                {currentLanguage === 'mr' ? 'क्लाउड SaaS मॉनिटर' : currentLanguage === 'hi' ? 'क्लाउड SaaS मॉनिटर' : 'Cloud SaaS & Security Center'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                {currentLanguage === 'mr'
                    ? 'सिस्टम कनेक्टिव्हिटी, सुरक्षित बॅकअप रेकॉर्ड आणि ऑनलाईन डेटा सिंक स्थितीचे निरीक्षण करा.'
                    : currentLanguage === 'hi'
                    ? 'सिस्टम कनेक्टिविटी, सुरक्षित बैकअप रिकॉर्ड और ऑनलाइन डेटा सिंक स्थिति की निगरानी करें।'
                    : 'Monitor server connectivity latency, offline database cache size, and trigger secure AWS backups.'}
            </Typography>

            <Grid container spacing={4} sx={{ mb: 4 }}>
                {/* 1. Sync Status Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <CloudQueueIcon sx={{ fontSize: 32, color: '#008751' }} />
                                <Chip label="ONLINE" size="small" color="success" sx={{ fontWeight: 800 }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                {currentLanguage === 'mr' ? 'क्लाउड कनेक्टिव्हिटी' : currentLanguage === 'hi' ? 'क्लाउड कनेक्टिविटी' : 'Cloud Connectivity'}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>
                                AWS-WEST-1 (Active)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 2. Latency Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <WifiIcon sx={{ fontSize: 32, color: '#008751' }} />
                                <Chip label="FAST" size="small" color="success" sx={{ fontWeight: 800 }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                {currentLanguage === 'mr' ? 'कनेक्शन लेटन्सी' : currentLanguage === 'hi' ? 'कनेक्शन लेटेंसी' : 'Server Latency'}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>
                                24 ms
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 3. Cache Storage */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <StorageIcon sx={{ fontSize: 32, color: '#008751' }} />
                                <Chip label="LOCAL" size="small" sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                {currentLanguage === 'mr' ? 'लोकल कॅशे स्टोरेज' : currentLanguage === 'hi' ? 'लोकल कैश स्टोरेज' : 'Offline Cache Size'}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>
                                4.82 MB
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 4. Automated Backup Status */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <AutorenewIcon sx={{ fontSize: 32, color: '#008751' }} />
                                <Chip label="AUTO" size="small" color="primary" sx={{ fontWeight: 800, bgcolor: '#008751' }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                {currentLanguage === 'mr' ? 'ऑटोमॅटिक बॅकअप' : currentLanguage === 'hi' ? 'ऑटोमैटिक बैकअप' : 'Auto Backup Sync'}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>
                                Hourly (Enabled)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Simulated terminal console */}
            <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', mb: 4 }}>
                <Box sx={{ p: 2, bgcolor: '#1e293b', color: '#f8fafc', display: 'flex', alignItems: 'center', gap: 1, borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
                    <TerminalIcon sx={{ color: '#10b981' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, fontFamily: 'monospace' }}>
                        Dinexa-SaaS-Agent-Console
                    </Typography>
                </Box>
                <Box sx={{ bgcolor: '#0f172a', p: 3, height: 220, overflowY: 'auto', borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}>
                    {logs.map((log, i) => (
                        <Typography 
                            key={i} 
                            sx={{ 
                                fontFamily: 'monospace', 
                                fontSize: '0.85rem', 
                                color: i === 0 ? '#10b981' : '#cbd5e1', 
                                mb: 1,
                                borderLeft: i === 0 ? '3px solid #10b981' : 'none',
                                pl: i === 0 ? 1 : 0
                            }}
                        >
                            {log}
                        </Typography>
                    ))}
                </Box>
            </Card>

            {/* Manual sync command controller */}
            <Card sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                    {currentLanguage === 'mr' ? 'क्लाउड सिंक नियंत्रक' : currentLanguage === 'hi' ? 'क्लाउड सिंक नियंत्रक' : 'Manual Database Backup Sync'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                    {currentLanguage === 'mr'
                        ? 'तथ्य गोळा करा आणि त्वरित AWS सर्व्हर वर पाठवा.'
                        : currentLanguage === 'hi'
                        ? 'डेटा एकत्र करें और तुरंत AWS सर्वर पर भेजें।'
                        : 'Initiate a complete manual sync of local SQLite transaction tables to cloud replication nodes.'}
                </Typography>

                {syncing ? (
                    <Box sx={{ width: '60%', mx: 'auto', my: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block' }}>
                            Syncing database cache: {progress}%
                        </Typography>
                        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#008751' } }} />
                    </Box>
                ) : (
                    <Button 
                        variant="contained" 
                        onClick={handleManualBackup}
                        sx={{ bgcolor: '#008751', fontWeight: 800, px: 4, '&:hover': { bgcolor: '#006f42' } }}
                    >
                        Sync Now to AWS Cloud
                    </Button>
                )}
            </Card>
        </Box>
    );
}
